import { useState, useCallback, useEffect, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import {
  ArrivalScreen,
  PreferencesScreen,
  TransitionScreen,
  ConsultationScreen,
  ThankYouScreen,
} from './screens';
import { LiveSessionProvider } from './context/LiveSessionProvider';
import { useScheduledCall } from './hooks/useScheduledCall';
import { useModeReporter } from './hooks/useModeReporter';
import { apiClient } from './services/api';
import { PhoneOffIcon } from './components/icons';
import { readResumeState, writeResumeState, clearResumeState } from './lib/sessionResume';
import { logger } from './services/logger';
import type { Screen, CommunicationMode } from './types/app';
import type { WidgetAuth } from './types/api';

function readRoomKeyFromLocation(): string | null {
  const pathMatch = window.location.pathname.match(/\/r\/([^/]+)/);
  return pathMatch ? pathMatch[1] : null;
}

function App() {
  // Extract room key from URL (lazy initializer avoids useEffect setState pattern)
  const [roomKey] = useState<string | null>(readRoomKeyFromLocation);

  // Read once, at mount: a record here means this tab was already inside the
  // consultation for this room. The meeting is running — send the customer back
  // into it rather than through Preferences and the permission gate again.
  const [resume] = useState(() => readResumeState(readRoomKeyFromLocation()));

  const [currentScreen, setCurrentScreen] = useState<Screen>(
    resume ? 'consultation' : 'arrival'
  );
  const [selectedMode, setSelectedMode] = useState<CommunicationMode | null>(
    resume?.mode ?? null
  );

  // Fetch scheduled call data
  const { data, isLoading, error, errorCode } = useScheduledCall(roomKey);

  // Recording-consent answer — set by TransitionScreen via onReady callback, seeded
  // from the resume record so a reload does not flash the question again before the
  // join response lands.
  const [recordingConsented, setRecordingConsented] = useState<boolean | null>(
    resume?.consented ?? null
  );

  // Portal is where the answer actually lives (Q1a added it to the join response),
  // so it overrides the sessionStorage seed once — and only once. Later the local
  // answer is the newer of the two: it is this tab that just took it, and the join
  // response is not re-fetched to learn about it.
  const serverConsentAdoptedRef = useRef(false);
  useEffect(() => {
    if (!data || serverConsentAdoptedRef.current) return;
    serverConsentAdoptedRef.current = true;
    // A resume record with no answer on the server means the POST never landed.
    // Nothing to do about it from here — the server reads "no answer" as "no" and
    // keeps this customer's voice out of the recording — but it should not pass
    // silently, because it is the shape a lost consent takes.
    if (data.recordingConsent === null && resume?.consented != null) {
      logger.warn('[Q1] Portal holds no recording-consent answer; the local one never landed.');
    }
    setRecordingConsented(data.recordingConsent);
  }, [data, resume?.consented]);

  // The appointment is over server-side (410) — the resume record is stale and would
  // otherwise keep bouncing a returning customer into a consultation that cannot load.
  useEffect(() => {
    if (errorCode === 'gone') clearResumeState(roomKey);
  }, [errorCode, roomKey]);

  // W5: the same change also goes to Portal, so the seller learns the customer
  // moved. Debounced and de-duplicated inside the hook — see useModeReporter.
  const reportMode = useModeReporter(roomKey);

  // Keep the record in step with the mode the customer actually ends up in —
  // switches, degradations and rolled-back failures alike.
  //
  // Two consumers, deliberately side by side: the session record that survives a
  // reload (S3) and the report to Portal (W5). Both want exactly the same event —
  // "the mode the customer is actually in now" — so neither wraps the other.
  const handleConsultationModeChange = useCallback((mode: CommunicationMode) => {
    writeResumeState(roomKey, { mode, consented: recordingConsented });
    reportMode(mode);
  }, [roomKey, recordingConsented, reportMode]);

  // Screen navigation handlers
  const handleArrivalContinue = useCallback(() => {
    setCurrentScreen('preferences');
  }, []);

  const handlePreferencesSelect = useCallback((mode: CommunicationMode) => {
    setSelectedMode(mode);
    setCurrentScreen('transition');
  }, []);

  const handleTransitionReady = useCallback((resolvedMode?: CommunicationMode, consented?: boolean) => {
    if (resolvedMode) {
      setSelectedMode(resolvedMode);
    }
    if (consented !== undefined) {
      setRecordingConsented(consented);
    }

    // W7: chat-only is the one entry path where the customer never connects to
    // the Twilio room, so Portal's `participant-connected` webhook never fires
    // and the appointment would look like a no-show. Report the arrival here —
    // this is the single funnel into the consultation, so it cannot double-fire.
    // Every other mode is covered by the webhook (including camera-denied, which
    // still connects audio-only).
    const effectiveMode = resolvedMode ?? selectedMode;
    if (effectiveMode === 'chat' && roomKey) {
      void apiClient.markCustomerJoined(roomKey);
    }

    // Arm the resume record at the single funnel into the consultation, so a reload
    // from any point inside the meeting comes straight back here.
    writeResumeState(roomKey, {
      mode: effectiveMode ?? 'video',
      consented: consented ?? recordingConsented,
    });

    setCurrentScreen('consultation');
  }, [selectedMode, roomKey, recordingConsented]);

  const handleEndCall = useCallback(() => {
    // The appointment is over — a later reload must get the normal entry, not a
    // resurrected consultation.
    clearResumeState(roomKey);
    setCurrentScreen('thankyou');
  }, [roomKey]);

  const recordingEnabled = data?.recordingEnabled ?? false;

  // Construct WidgetAuth for feedback form on ThankYouScreen (SPEC-041c)
  const feedbackAuth: WidgetAuth | undefined = data?.customerSession
    ? {
        accessToken: data.customerSession.access_token,
        refreshToken: data.customerSession.refresh_token,
        serviceId: data.customerSession.service_id,
      }
    : undefined;

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-brand-gold border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your appointment...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !data) {
    const isGone = errorCode === 'gone';

    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center px-6">
        <div className="text-center">
          {isGone ? (
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <PhoneOffIcon size={32} className="text-gray-400" />
            </div>
          ) : (
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-red-500"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4M12 16h.01" />
              </svg>
            </div>
          )}
          <h1 className="text-xl font-serif text-brand-navy mb-2">
            {isGone ? 'This Call Has Ended' : 'Unable to Load Appointment'}
          </h1>
          <p className="text-gray-600 mb-4">
            {isGone
              ? 'This appointment is no longer available.'
              : error || 'The appointment could not be found.'}
          </p>
          {!isGone && (
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-brand-gold text-white rounded-lg hover:bg-brand-gold-dark"
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    // One video room and one conversation for the whole visit, held above the
    // screens. TransitionScreen opens the room and ConsultationScreen inherits it,
    // so the customer joins once instead of leaving and re-joining on the way in.
    <LiveSessionProvider>
      <AnimatePresence mode="wait">
        {currentScreen === 'arrival' && (
          <ArrivalScreen
            key="arrival"
            organization={data.organization}
            scheduledAt={data.scheduledAt}
            onContinue={handleArrivalContinue}
          />
        )}

        {currentScreen === 'preferences' && (
          <PreferencesScreen
            key="preferences"
            onSelect={handlePreferencesSelect}
            sellerMode={data.sellerMode}
          />
        )}

        {currentScreen === 'transition' && (
          <TransitionScreen
            key="transition"
            seller={data.seller}
            videoToken={data.credentials.videoToken}
            roomName={data.credentials.roomName}
            roomKey={roomKey!}
            recordingEnabled={data.recordingEnabled}
            initialConsent={recordingConsented}
            selectedMode={selectedMode ?? 'video'}
            onReady={handleTransitionReady}
          />
        )}

        {currentScreen === 'consultation' && selectedMode && (
          <ConsultationScreen
            key="consultation"
            credentials={data.credentials}
            seller={data.seller}
            organization={data.organization}
            initialMode={selectedMode}
            customerSession={data.customerSession}
            callId={data.callId}
            recordingEnabled={recordingEnabled}
            recordingConsent={recordingConsented}
            onEndCall={handleEndCall}
            onModeChange={handleConsultationModeChange}
          />
        )}

        {currentScreen === 'thankyou' && (
          <ThankYouScreen
            key="thankyou"
            organization={data.organization}
            callId={data.callId}
            auth={feedbackAuth}
          />
        )}
      </AnimatePresence>
    </LiveSessionProvider>
  );
}

export default App;
